import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CaretLeft } from "@phosphor-icons/react";
import useDocumentMeta from "../lib/useDocumentMeta";

const API = process.env.REACT_APP_BACKEND_URL;

function FaqJsonLd({ faq }) {
  useEffect(() => {
    if (!faq?.length) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "seo-page-faq-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((q) => ({
        "@type": "Question",
        name: q.q,
        acceptedAnswer: { "@type": "Answer", text: q.a },
      })),
    });
    const old = document.getElementById("seo-page-faq-jsonld");
    if (old) old.remove();
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById("seo-page-faq-jsonld");
      if (el) el.remove();
    };
  }, [faq]);
  return null;
}

export default function SEOPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    setData(null);
    setError(null);
    fetch(`${API}/api/seo/page/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error("notfound");
        return r.json();
      })
      .then(setData)
      .catch(() => setError("notfound"));
  }, [slug]);

  const page = data?.page;
  const dynamicKw = data?.dynamic_keywords || [];

  useDocumentMeta({
    title: page?.title,
    description: page?.description,
    keywords: page
      ? page.keywords + (dynamicKw.length ? ", " + dynamicKw.join(", ") : "")
      : undefined,
    canonical: data?.url,
  });

  if (error === "notfound") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground px-6">
        <div className="eyebrow text-[hsl(var(--secondary))] mb-3">404</div>
        <h1 className="font-serif text-4xl mb-4">Guide not found</h1>
        <Link
          to="/seo"
          className="text-[hsl(var(--primary))] underline-offset-4 hover:underline text-sm"
          data-testid="seo-page-back-to-index"
        >
          ← Back to all guides
        </Link>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <FaqJsonLd faq={page.faq} />
      <nav className="border-b hairline">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/seo"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            data-testid="seo-page-back"
          >
            <CaretLeft size={14} weight="bold" /> All guides
          </Link>
          <Link
            to="/register"
            className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2"
            data-testid="seo-page-nav-cta"
          >
            Try free <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </nav>

      <article
        className="max-w-3xl mx-auto px-6 py-16"
        data-testid={`seo-page-${slug}`}
      >
        <div className="eyebrow text-[hsl(var(--secondary))] mb-3">
          Estima · 2026 India guide
        </div>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-6">
          {page.h1}
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed mb-10">
          {page.intro}
        </p>

        {page.sections.map((sec) => (
          <section key={sec.heading} className="mb-10">
            <h2 className="font-serif text-2xl mb-4">{sec.heading}</h2>
            <ul className="space-y-2 list-disc list-outside pl-5 text-[15px] leading-relaxed">
              {sec.items.map((it) => (
                <li key={`${sec.heading}-${it.slice(0, 40)}`}>{it}</li>
              ))}
            </ul>
          </section>
        ))}

        <section
          className="mt-12 mb-10"
          data-testid="seo-page-faq"
          itemScope
          itemType="https://schema.org/FAQPage"
        >
          <h2 className="font-serif text-2xl mb-4">Frequently asked</h2>
          <div className="rounded-xl border hairline overflow-hidden bg-card">
            {page.faq.map((q, idx) => {
              const open = idx === openFaq;
              return (
                <div
                  key={q.q}
                  className={`border-b hairline last:border-b-0 ${
                    open ? "bg-[hsl(var(--muted))]/40" : ""
                  }`}
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? -1 : idx)}
                    className="w-full text-left px-5 py-4 font-serif text-base hover:bg-[hsl(var(--muted))]/30"
                    aria-expanded={open}
                    data-testid={`seo-faq-toggle-${idx}`}
                  >
                    <span itemProp="name">{q.q}</span>
                  </button>
                  {open && (
                    <div
                      className="px-5 pb-5 -mt-2 text-sm text-muted-foreground leading-relaxed"
                      itemScope
                      itemProp="acceptedAnswer"
                      itemType="https://schema.org/Answer"
                      data-testid={`seo-faq-answer-${idx}`}
                    >
                      <p itemProp="text">{q.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-12 rounded-xl bg-[hsl(var(--primary))] text-primary-foreground p-8 text-center">
          <h2 className="font-serif text-2xl mb-3">{page.cta_text}</h2>
          <Link
            to="/register"
            className="btn-secondary inline-flex items-center gap-2 px-6 py-3 mt-2"
            data-testid="seo-page-cta"
          >
            Start free — 6-month trial · No card required{" "}
            <ArrowRight size={14} weight="bold" />
          </Link>
          <p className="text-[11px] opacity-80 mt-4 max-w-xl mx-auto leading-relaxed">
            Estimates only — not investment, legal or tax advice. Please
            consult a SEBI-registered investment adviser or a Chartered
            Accountant before any property or financial decision.
          </p>
        </section>
      </article>

      <footer className="border-t hairline">
        <div className="max-w-3xl mx-auto px-6 py-8 text-xs text-muted-foreground flex flex-wrap gap-6 justify-between">
          <div>© {new Date().getFullYear()} Estima · INR-first India</div>
          <div className="flex gap-4">
            <Link to="/disclaimer">Disclaimer</Link>
            <Link to="/legal/privacy">Privacy</Link>
            <Link to="/seo">All guides</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
